<template>
  <BuilderMatchMedia v-slot="{ matches: isDesktop }" device="desktop">
    <div
      tabindex="0"
      class="card-product"
      data-testid="card-product"
      role="button"
      @click="onSelect"
      @keydown.enter.space="onSelect"
    >
      <Row data-testid="row-title" :has-line="false">
        <div slot="top-left" data-testid="risk-score">
          <TagRiskLevel :risk-grade="instrument.riskScore.value" />
        </div>
        <div slot="bottom-left" data-testid="title" class="font-size-title">
          {{ instrument.name }}
        </div>

        <div v-if="addProduct && isDesktop" slot="icon-right">
          <ButtonIconContextual
            data-testid="button-add-product"
            class="add-button"
            label="Adicionar produto"
            aria-label="Adicionar produto"
            icon="EF0010"
            size="small"
            :loading="productInLoadingState === instrument.apiId"
            :disabled="
              instrument.isAdded || productInLoadingState !== '' ? true : false
            "
            @click.stop="addProductEvent(instrument)"
          />
        </div>
      </Row>
      <hr class="card-products-divider" />
      <Grid :columns="[2, 3, 3, 2]">
        <Row data-testid="row-benchmarkFormatted" :has-line="false">
          <div slot="top-left" class="font-size label">Rentabilidade</div>
          <div v-if="!isDesktop" slot="top-right" class="font-size value">
            {{ benchmarkFormatted }}
          </div>
          <div v-else slot="bottom-left" class="font-size value">
            {{ benchmarkFormatted }}
          </div>
        </Row>
        <Row
          :class="{ 'row-center': isDesktop }"
          data-testid="row-minimumInitialInvestment"
          :has-line="false"
        >
          <div slot="top-left" class="font-size label">Depósito inicial</div>
          <div v-if="!isDesktop" slot="top-right" class="font-size value">
            {{ instrument.minimumInitialInvestment | money }}
          </div>
          <div v-else slot="bottom-left" class="font-size value">
            {{ instrument.minimumInitialInvestment | money }}
          </div>
        </Row>
        <Row
          :class="{ 'row-center': isDesktop }"
          data-testid="row-minimumFinancialMovement"
          :has-line="false"
        >
          <div slot="top-left" class="font-size label">Mov. mínima</div>
          <div v-if="!isDesktop" slot="top-right" class="font-size value">
            {{ instrument.minimumFinancialMovement | money }}
          </div>
          <div v-else slot="bottom-left" class="font-size value">
            {{ instrument.minimumFinancialMovement | money }}
          </div>
        </Row>
        <Row
          :class="{ 'row-end': isDesktop }"
          data-testid="row-liquidity"
          :has-line="false"
        >
          <div slot="top-left" class="font-size label">Resgate em até</div>
          <div v-if="!isDesktop" slot="top-right" class="font-size value">
            {{ liquidityFormatted }}
          </div>

          <div v-else slot="bottom-left" class="font-size value">
            {{ liquidityFormatted }}
          </div>
        </Row>
      </Grid>
    </div>
  </BuilderMatchMedia>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { moneyViewer } from '../../helpers';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import { Grid, Row, ButtonIconContextual } from '@warrenbrasil/nebraska-web';

import { BuilderMatchMedia } from '../BuilderMatchMedia';

import TagRiskLevel from '../TagRiskLevel/TagRiskLevel.vue';
import { IInstrument } from '../../services/types/models/IBffInvestments';

@Component({
  components: {
    Row,
    Grid,
    TagRiskLevel,
    BuilderMatchMedia,
    ButtonIconContextual
  },
  filters: {
    money: moneyViewer
  }
})
export default class CardInstrument extends Vue {
  @Prop({ type: Object, required: true })
  public instrument!: IInstrument;

  @Prop({ type: Boolean, required: false, default: false })
  public allowAddButton!: boolean;

  @Prop({ type: Boolean, required: false, default: true })
  public addProduct?: boolean;

  @Prop({ type: String, required: false, default: true })
  public productInLoadingState!: string;

  private NebraskaColors = NebraskaColors;

  private isDesktop = false;

  get liquidityFormatted() {
    return this.instrument.liquidity?.formatted;
  }

  private onSelect() {
    this.$emit('handle-instrument-click', this.instrument);
  }

  private addProductEvent(instrument: IInstrument) {
    if (instrument.isAdded) {
      return;
    }

    this.$emit('handle-instrument-add-clicked', instrument);
  }

  private get benchmarkFormatted() {
    return this.instrument.profitability ?? '-';
  }
}
</script>

<style lang="less" scoped>
/deep/ .row-item > div {
  padding: 0 !important;
  margin: 0 !important;
}

/deep/ .row-item > div .starts {
  margin-bottom: @size-spacing-x150 !important;
}

/deep/ .row-item {
  background-color: transparent !important;
}

.card-product {
  background: @background-secondary;
  width: 100%;
  padding: @size-spacing-x400;
  cursor: pointer;
  border: solid 1px @divider-primary;
  border-radius: 8px;
  margin-bottom: @size-spacing-x300;

  .add-button {
    z-index: 0;
    &:focus {
      box-shadow: 0 0;
    }
  }
}

.card-product:hover {
  background: @background-primary;
}

.card-products-divider {
  border-top: solid 1px @divider-primary!important;
}

.font-size-title {
  font-size: 20px !important;
}

.font-size {
  font-size: 16px !important;
}

.value {
  color: @text-primary;
}

.label {
  color: @text-secondary;
}

.row-end {
  justify-self: flex-end;
}

.row-center {
  justify-self: center;
}

.row-margin {
  margin-right: 20px;
}
</style>
